import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/react-hooks";
import { Alert, Spin } from "antd";
import gql from "fraql";
import get from "lodash/get";
import omit from "lodash/omit";
import { useTenantPicker } from "../TenantPickerContext"; 
import FormFieldCheckboxGroup from "./FormFieldCheckboxGroup";
import FormFieldFormItem from "./FormFieldFormItem";
import FormFieldSelect from "./FormFieldSelect";
import getActiveArchivedOptions from "./getActiveArchivedOptions";


const GET_TENANTS = gql`
  query FormFieldTenant_GetTenants {
    Tenant(order_by: { name: asc }) {
      id
      key
      name
    }
  }
`;

export const DISPLAY_MODES = {
  checkboxList: "checkboxList",
  select: "select",
};

function FormFieldTenant(props) {
  const { meta, query, queryOptions, disabled: defaultDisabled } = props;
  const { setTenantFilter } = useTenantPicker();
  const [disabled, setDisabled] = useState(defaultDisabled);

  const handleTenantChange = (id) => {
    setTenantFilter(id);

    if (meta.handleSelectChange){
      meta.handleSelectChange(id);
    }
  }

  const metaWithoutChange = {...omit(meta, ["handleSelectChange"])};

  const morphMeta = {
    ...metaWithoutChange,
    handleSelectChange: handleTenantChange
  }

  const displayDefaultLabel = get(meta, "displayDefaultLabel", true);
  const displayMode = get(meta, "displayMode", DISPLAY_MODES.select);

  const { loading, error, data } = useQuery(query, queryOptions);

  const options = useMemo(() => getActiveArchivedOptions({ allRecords: get(data, "Tenant", []) }), [data]);

  useEffect(()=>{
    if (options.length === 1){
      setDisabled(true);
    }
  }, [options])

  if (loading && !data) {
    return (
      <FormFieldFormItem {...props} displayDefaultLabel={displayDefaultLabel} displayForInput={false}>
        <Spin size="small" className="form-field-spin" />
      </FormFieldFormItem>
    );
  }

  if (error) {
    return (
      <FormFieldFormItem {...props} displayDefaultLabel={displayDefaultLabel} displayForInput={false}>
        <Alert message="Tenants failed to load" type="error" className="form-field-alert" />
      </FormFieldFormItem>
    );
  }

  switch (displayMode) {
    case DISPLAY_MODES.checkboxList: {
      return <FormFieldCheckboxGroup {...omit(props, ["disabled"])} disabled={disabled} meta={{ ...morphMeta, options }} />;
    }
    case DISPLAY_MODES.select: {
      return <FormFieldSelect {...omit(props, ["disabled"])} disabled={disabled} loading={loading} meta={{ ...morphMeta, options }} />;
    }
    default: {
      console.error("Unsupported display mode", displayMode);
      return null;
    }
  }
}

FormFieldTenant.propTypes = {
  name: PropTypes.string.isRequired,
  meta: PropTypes.shape({
    label: PropTypes.string,
    required: PropTypes.bool,
    helpText: PropTypes.node,
    displayDefaultLabel: PropTypes.bool,
    displayMode: PropTypes.string,
    handleSelectChange: PropTypes.func,
  }).isRequired,
  query: PropTypes.any,
  queryOptions: PropTypes.object,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
};

FormFieldTenant.defaultProps = {
  query: GET_TENANTS,
  queryOptions: {},
  readOnly: false,
  disabled: false,
};

export default FormFieldTenant;
